import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import React from 'react';

import {Badge} from '@albert-io/atomic';
import type {BadgeStatuses as BadgeStatus} from '@albert-io/atomic/atoms/Badge/Badge.react';
import type {StudentAssignment} from './StudentAssignment.types';
import {StudentAssignmentStatus} from 'gql/types';

type AssignmentStatus =
  | 'graded'
  | 'gradePending'
  | 'inProgress'
  | 'notStarted'
  | 'pastDue'
  | 'submitted';

interface Props {
  grade: StudentAssignment['grade'];
  status: StudentAssignment['status'];
  /**
   * False only when the assignment has manually-graded questions the teacher has not released.
   * The grade itself is NOT gated on release — it becomes visible as soon as the assignment
   * closes — so without this the row would show a percentage the student is not entitled to see.
   */
  gradesReleased: StudentAssignment['gradesReleased'];
}

const assignmentStatusToBadgeStatus = new Map<AssignmentStatus, BadgeStatus>([
  ['graded', 'neutral'],
  ['gradePending', 'neutral'],
  ['inProgress', 'info'],
  ['notStarted', 'neutral'],
  ['pastDue', 'negative'],
  ['submitted', 'positive']
]);

export default function StatusBadge({grade, status, gradesReleased}: Props) {
  'use memo';

  let assignmentStatus: AssignmentStatus;

  // Ahead of the graded branch, and covering more than the submitted case: an unsubmitted
  // assignment past its due date is `closed`, which also resolves a visible grade.
  if (gradesReleased === false && (status === StudentAssignmentStatus.Completed || !isNil(grade))) {
    assignmentStatus = 'gradePending';
  } else if (!isNil(grade)) {
    assignmentStatus = 'graded';
  } else if (status === StudentAssignmentStatus.Completed) {
    assignmentStatus = 'submitted';
  } else if (status === StudentAssignmentStatus.PastDue) {
    assignmentStatus = 'pastDue';
  } else if (status === StudentAssignmentStatus.InProgress) {
    assignmentStatus = 'inProgress';
  } else {
    assignmentStatus = 'notStarted';
  }

  return (
    <Badge
      emphasis='subtle'
      roundness='square'
      status={assignmentStatusToBadgeStatus.get(assignmentStatus)}
      icon={assignmentStatus === 'gradePending' ? 'arrows-rotate' : undefined}
      iconStyle='regular'
    >
      {/* Keyed off the resolved status, not `grade`: a pending grade has a value and must not
          print it. `startCase` already yields "Grade Pending". */}
      {assignmentStatus === 'graded' && !isNil(grade)
        ? `Grade: ${grade.toLocaleString(undefined, {style: 'percent'})}`
        : startCase(assignmentStatus)}
    </Badge>
  );
}
